import { apiBaseUrl } from "utils/constants";
import { isProduction } from "scripts/application/setting";

/**
 * Matomo integration for the AI Mode SPA.
 *
 * Two transports live side by side:
 *
 * 1. **The official JavaScript tracker** (`matomo.js`). Heatmaps and Session
 *    Recordings are *recorded in the browser* by the Heatmap & Session
 *    Recording plugin, which ships inside `matomo.js` — no amount of
 *    server-side proxying of `matomo.php` can produce them, which is why the
 *    original event proxy below could never grow those features. When a
 *    tracker URL is configured this transport also carries page views and
 *    events, so recordings, heatmaps, page views and events all share one
 *    visitor id and one visit.
 * 2. **The server-side event proxy** (`/api/v1/chatbot/matomo`), the original
 *    transport. It is kept as the fallback for events when `matomo.js` fails
 *    to load (ad blockers block `matomo.js` by URL pattern), and it is what
 *    still runs when no tracker URL is configured at all.
 *
 * Configuration (see `frontend_legacy/.env`):
 *
 * - `REACT_APP_MATOMO_URL` — base URL of the Matomo install. Empty (the
 *   default) leaves the JS tracker off and the app behaves exactly as before.
 *   Two values are supported:
 *     - `https://matomo.har.com/` — straight to Matomo, the documented setup.
 *     - `/api/v1/chatbot/matomo/` — the backend's first-party mirror, which
 *       serves the same files from our own origin and forwards the visitor's
 *       real IP (see `matomo_route.py`). It defeats blocker rules written
 *       against the Matomo *host*; it does not defeat the generic path rules
 *       that filter lists also carry (`/matomo.js`, `/matomo.php`), because
 *       the mirror keeps those file names so the plugin can derive its own
 *       config URL from the tracker URL. Opaque names would need the derived
 *       path solved too, and are a follow-up rather than a claim made here.
 * - `REACT_APP_MATOMO_SITE_ID` — site the app reports to (8), on either
 *   transport. The proxy used to report events to site 5, which was the wrong
 *   site; there is now one site id for everything AI Mode sends.
 * - `REACT_APP_MATOMO_SITE_ID_TEST` — site for non-production hosts. Empty
 *   keeps test and local traffic out of Matomo entirely, which is what the
 *   backend proxy has always done by refusing to forward outside production.
 */

declare global {
  interface Window {
    /**
     * Matomo's command queue. Commands pushed before `matomo.js` loads are
     * replayed by it in order, which is why everything below is a plain push
     * and nothing needs to wait for the script.
     */
    _paq?: unknown[][];
    /** The tracker itself, once `matomo.js` has run. Plugins compiled into it
     *  hang their static API off this object. */
    Matomo?: { HeatmapSessionRecording?: unknown };
  }
}

const MATOMO_PROXY_URL = `${apiBaseUrl}/api/v1/chatbot/matomo`;

/**
 * The site AI Mode reports to — page views, heatmaps, recordings and events,
 * on both transports. Site 8 is the correct one; the server-side proxy used to
 * report events to site 5, which was wrong, so nothing here is split by which
 * transport happened to carry a request.
 *
 * Falls back to 8 rather than to nothing, because a blank override would put
 * `idsite=` on the proxy transport, which used to carry a site id of its own.
 * Tracking is switched off with `REACT_APP_MATOMO_URL`, not by blanking this.
 */
const SITE_ID = (process.env.REACT_APP_MATOMO_SITE_ID ?? "").trim() || "8";

/** Site for test/local hosts. Empty (the default) disables tracking there. */
const TEST_SITE_ID = (process.env.REACT_APP_MATOMO_SITE_ID_TEST ?? "").trim();

const siteId = isProduction ? SITE_ID : TEST_SITE_ID;

/** Base URL of the Matomo files, always with a trailing slash. */
const matomoBaseUrl = resolveBaseUrl(process.env.REACT_APP_MATOMO_URL);

/** True when `matomo.js` should be loaded — i.e. heatmaps/recordings are on. */
export const matomoTrackerEnabled = Boolean(matomoBaseUrl && siteId);

function resolveBaseUrl(configured: string | undefined): string {
  const base = (configured ?? "").trim();
  if (!base) return "";
  // A path (the backend mirror) is resolved against the API host so it keeps
  // working when the SPA is served from a different origin in development.
  const absolute = base.startsWith("/") ? `${apiBaseUrl}${base}` : base;
  return absolute.endsWith("/") ? absolute : `${absolute}/`;
}

/* -------------------------------------------------------------------------- */
/* Bootstrap                                                                   */
/* -------------------------------------------------------------------------- */

type TrackerState = "idle" | "pending" | "ready" | "failed";

let trackerState: TrackerState = "idle";

/** Events pushed while `matomo.js` is still in flight, replayed via the proxy
 *  if it never arrives. `_paq` keeps its own copy for the happy path. */
const bufferedEvents: IMatomoEvent[] = [];
const MAX_BUFFERED_EVENTS = 50;

interface IMatomoEvent {
  category: string;
  action: string;
  name: string;
  url: string;
  uid?: string;
}

function paq(): unknown[][] {
  window._paq = window._paq ?? [];
  return window._paq;
}

/** Whether queueing a command can still lead anywhere. Once `matomo.js` has
 *  failed nothing replays `_paq`, so a push is only memory. */
function trackerUsable(): boolean {
  return matomoTrackerEnabled && trackerState !== "failed";
}

/**
 * Configure the tracker and start loading `matomo.js`. Idempotent, and a no-op
 * when no tracker URL is configured. Page views are *not* sent from here — the
 * router owns them (see `useMatomoPageView`), so the first one carries the same
 * canonical URL as every later one.
 */
export function initMatomo(): void {
  if (!matomoTrackerEnabled || trackerState !== "idle") return;
  trackerState = "pending";

  const _paq = paq();
  _paq.push(["setTrackerUrl", `${matomoBaseUrl}matomo.php`]);
  _paq.push(["setSiteId", siteId]);
  if (window.location.protocol === "https:") {
    _paq.push(["setSecureCookie", true]);
  }
  // A whole conversation happens inside one page view, so without a heartbeat
  // Matomo would record ~0s of time spent for most visits.
  _paq.push(["enableHeartBeatTimer"]);

  // --- Heatmap & Session Recording ---------------------------------------
  // Page rules are matched against the URL we report with `setCustomUrl`
  // (canonicalPageUrl below), not against the raw location, so a rule written
  // for `aimode.har.com/` also covers `/<session-uuid>`.
  _paq.push(["HeatmapSessionRecording::matchTrackerUrl"]);
  // Matomo restarts recording on every `trackPageView`. We drive that
  // explicitly from the router instead, so a future virtual page view (an
  // error or a download tracked as a page view) cannot cut a recording short.
  _paq.push(["HeatmapSessionRecording::disableAutoDetectNewPageView"]);
  // Off by default since plugin 3.2.0; pinned here so it cannot be turned on
  // from the Matomo UI without a code change. The composer is where visitors
  // type addresses and budgets.
  //
  // Note what this and the URL rules do *not* cover. Everything canonicalised
  // in this file is a tracker request URL — the Pages, Events and Referrers
  // reports, where a value is aggregated and searchable. A recording replays
  // the DOM, and the DOM holds the conversation: the messages themselves, and
  // hrefs built from the current query, such as `MlsSearchCard`'s chat link,
  // which carries `question`, `q` and `listing_filter` verbatim. That is the
  // accepted posture — chat text stays visible in replays because it is the
  // reason to watch one — but it means "prompts never reach Matomo" is only
  // true of the reports. Masking the message list and those links is the lever
  // if that changes; see `data-matomo-mask` on the account menus.
  _paq.push(["HeatmapSessionRecording::disableCaptureKeystrokes"]);

  loadTrackerScript();
}

function loadTrackerScript(): void {
  const script = document.createElement("script");
  script.async = true;
  script.src = `${matomoBaseUrl}matomo.js`;
  script.onload = () => {
    trackerState = "ready";
    // matomo.js replays `_paq`, so the buffered copies would be duplicates.
    bufferedEvents.length = 0;
    warnIfHeatmapPluginMissing();
  };
  script.onerror = () => {
    // Blocked or unreachable: `_paq` will never be replayed. Everything that
    // needs the browser (heatmaps, recordings) is lost for this visitor, but
    // events can still go out through the backend proxy.
    trackerState = "failed";
    // Sent together, in the order they happened. Which of them reaches Matomo
    // first no longer matters: every one carries the referrer, so whichever
    // request ends up creating the visit reports where it came from (see
    // `sendEventViaProxy`).
    bufferedEvents.splice(0, bufferedEvents.length).forEach(sendEventViaProxy);
    // Nothing will ever read the queue now; a long session on a blocked
    // browser would otherwise grow it forever, four commands per navigation.
    window._paq = [];
  };

  const firstScript = document.getElementsByTagName("script")[0];
  if (firstScript?.parentNode) {
    firstScript.parentNode.insertBefore(script, firstScript);
  } else {
    document.head.appendChild(script);
  }
}

/**
 * Heatmaps and recordings are the plugin's code *inside* `matomo.js`, which the
 * Matomo server only compiles in while that file is writable by the web server.
 * Where it is not, every `HeatmapSessionRecording::*` command queued above is
 * silently ignored — page views and events keep working, so nothing else would
 * ever say that the feature this integration exists for is off.
 *
 * Reported rather than repaired: the plugin also ships as a standalone
 * `plugins/HeatmapSessionRecording/tracker.min.js` (which the backend mirror
 * serves), but loading it on top of a `matomo.js` that already carries the
 * plugin risks recording everything twice. Making `matomo.js` writable is the
 * documented fix and the one that cannot double-count.
 */
function warnIfHeatmapPluginMissing(): void {
  if (window.Matomo?.HeatmapSessionRecording) return;
  // Deliberately phrased as "not visible" rather than "not compiled in": the
  // absence of the namespace is evidence, not proof. Both causes are named so
  // this cannot send someone to change file permissions on a Matomo where the
  // real answer is that nothing is configured to record yet.
  console.warn(
    "[matomo] No Heatmap & Session Recording API on the loaded tracker: page " +
      "views and events are tracked, heatmaps and recordings are not. Either " +
      "no heatmap or session recording is configured for this site, or " +
      "matomo.js is not writable by the web server on the Matomo host and so " +
      "carries no plugin code. Check the first before changing the second.",
  );
}

/* -------------------------------------------------------------------------- */
/* Page URLs                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * A chat session id, in every shape the backend mints one.
 *
 * Usually a bare uuid4, but a conversation that failed gets a labelled one:
 * `error_<uuid>` from `post_chat.py` and `insert_error.py`, `home_error_<uuid>`
 * from `query_urls.py`. Those threads are saved like any other, so they come
 * back in the sidebar and are opened at `/error_<uuid>` — and a rule that only
 * knew the bare form left the id in the reported URL, giving every failed
 * conversation its own page and its own one-visit heatmap, and cut the
 * recording at the first message because the id appearing did not look like
 * an id appearing.
 */
const SESSION_SEGMENT =
  /^(?:home_error_|error_)?[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Query parameters kept on a reported page URL. Campaign attribution only —
 * `pk_*` also covers `pk_hsr_forcesample`, the flag used to force a recording
 * while verifying a rollout.
 *
 * An allowlist rather than a denylist because the app puts the visitor's own
 * words in the URL: `question`, `q` and `listing_filter` carry the prompt (and
 * encoded polygons, which are enormous). Matomo has no business storing those
 * in a page URL — the conversation is already persisted server side.
 */
const CAMPAIGN_PARAM = /^(utm_|mtm_|pk_|piwik_|matomo_)/i;

/**
 * Query parameters that change which layout renders, and so are part of what
 * page this is rather than data about the visitor (see `useQueryFlags`).
 *
 * They are kept for two reasons. A heatmap aggregates by URL, and clicks in
 * the embedded view — no header, no sidebar — land in different places than
 * the same clicks in the full one, so pooling them produces an average of two
 * different pages. And Matomo draws the overlay on the page it loads from that
 * URL: reporting an embedded recording as plain `/` would render it against a
 * layout the visitor never saw.
 */
const LAYOUT_PARAM = new Set([
  "embed",
  "hide_header",
  "hide_footer",
  "hide_share",
  "hide_user",
  "readonly",
  "timeline",
  // DefaultLayout puts a `<source>-embed` class on the body for it, so the
  // page renders differently per host application.
  "source",
]);

/** The values `source` is given layout meaning for (`eApplicationSource`). */
const APPLICATION_SOURCE = new Set([
  "admin",
  "share",
  "myaccount",
  "listing-detail",
]);

/**
 * The spellings that *enable* a flag — exactly what `useQueryFlags`'s `asBool`
 * accepts, and nothing else.
 *
 * `?embed=0`, `?embed=false` and the bare `?embed` all leave `asBool` false,
 * so they render the identical page to no `embed` at all. Treating them as a
 * layout would report one rendered page under several URLs, splitting its
 * heatmap, and make the move between those URLs look like a navigation worth
 * cutting a recording for. A flag that is not on is a flag that is not there.
 */
const BOOLEAN_FLAG = /^(1|true|yes)$/i;

/**
 * Whether a layout parameter's *value* is one the app gives meaning to.
 *
 * The allowlist is by key, and a key alone is not a guarantee: `?source=secret`
 * is not a layout variant, it is arbitrary text that would ride into Matomo
 * through a door opened for something else. Anything unrecognised is dropped
 * rather than reported, on the same reasoning as everywhere else here — what
 * cannot be shown to be safe does not go.
 *
 * A value counts only where every consumer of it agrees, which is stricter
 * than any single one of them. `source` is read four ways: `DefaultLayout`
 * trims and lower-cases it for the body class, while `MlsSearchCard`,
 * `ListingChatbotPage` and `useSignInHandlers` compare `useQueryFlags`' raw
 * value against `"listing-detail"` exactly. So `?source=%20listing-detail%20`
 * gets the listing-detail body class *and* the links of a page that is not
 * listing-detail — a rendering no canonical URL describes honestly, and one
 * this therefore declines to report as a layout at all. Only the exact
 * spelling counts. Booleans are tested raw, because `asBool` does.
 */
function isLayoutValue(key: string, value: string): boolean {
  if (key === "source") return APPLICATION_SOURCE.has(value);
  return BOOLEAN_FLAG.test(value);
}

/**
 * One spelling for every value that means the same layout.
 *
 * `?embed=TRUE` and `?embed=1` render the same page, since `asBool` is
 * case-insensitive, and reported apart they would split that page's heatmap by
 * a difference the visitor never made — and make a spelling-only change read
 * as a navigation worth cutting a recording for. `source` needs nothing here:
 * only its exact spelling was accepted.
 */
function canonicalLayoutValue(key: string, value: string): string {
  return key === "source" ? value : "1";
}

/**
 * The layout parameters that survive, in the form they are reported in.
 *
 * One function, because the reported URL and the de-duplication key have to
 * agree on all four questions here — which keys count, which value of a
 * repeated key, whether that value means anything, and how it is spelled —
 * and every round that asked them separately found them disagreeing on one.
 */
function keptLayoutParams(params: URLSearchParams): URLSearchParams {
  const kept = new URLSearchParams();
  [...new Set(params.keys())]
    .filter((key) => LAYOUT_PARAM.has(key))
    .sort()
    .forEach((key) => {
      // The first occurrence, because `useQueryFlags` reads these with `get`:
      // "?embed=0&embed=1" renders the plain layout, whatever follows.
      const value = params.get(key) ?? "";
      if (isLayoutValue(key, value)) {
        kept.append(key, canonicalLayoutValue(key, value));
      }
    });
  return kept;
}

/**
 * The backend's entry point. The router redirects it to "/" on mount, so the
 * two are one arrival and one page.
 *
 * Exactly those two segments: "/listing/search/<session>" is not the entry
 * point, it is a listing assistant whose harid happens to read "search".
 */
function isBackendEntryPoint(pathname: string): boolean {
  // Case-folded here rather than by the caller. `canonicalRoutePath` and
  // `pageKey` both ask this question, and having each normalise on its own is
  // how "/Listing/Search" ended up reported as "/" and keyed as itself — one
  // arrival counted as two, cutting the recording it had just started.
  return pathname.toLowerCase() === "/listing/search";
}

/**
 * The URL a page view is reported under.
 *
 * Chat session ids are dropped: they are unique per conversation, so leaving
 * them in would give every visitor their own page in the Pages report and
 * their own one-visit heatmap. Matomo also renders a heatmap by loading the
 * page in an iframe, and `/<session-uuid>` only resolves for the visitor who
 * owns it — `/` renders the same chat shell for anyone.
 *
 * The hash is dropped since the app does not route on it, and the query string
 * is reduced to campaign parameters.
 */
export function canonicalPageUrl(href: string = window.location.href): string {
  const url = new URL(href, window.location.origin);
  url.hash = "";
  url.pathname = canonicalPathname(url.pathname);
  url.search = reportableQuery(url.searchParams);

  return url.toString();
}

/**
 * The query string of one of our own URLs, reduced to what may be reported:
 * the campaign parameters an attribution report is built on, and the layout
 * flags that say which variant of the page was rendered. Everything else is
 * dropped — a question, a chat id, an error message, a filter — because what
 * cannot be shown to be safe does not go.
 *
 * One function for every one of our URLs, wherever it turns up: the address
 * bar, an event, or a referrer from a sibling HAR host. `www.har.com/aimode/`
 * is a different origin from `aimode.har.com`, so a visitor coming back into
 * the app from a page of ours on the other host arrives as an *external*
 * referrer — and the query the page URL rules scrub would have ridden in
 * whole through the door held open for attribution.
 */
function reportableQuery(params: URLSearchParams): string {
  const kept = new URLSearchParams();
  params.forEach((value, key) => {
    if (CAMPAIGN_PARAM.test(key)) kept.append(key, value);
  });
  keptLayoutParams(params).forEach((value, key) => {
    kept.append(key, value);
  });
  // Sorted, so the order the visitor happened to arrive with cannot split one
  // page's report in two: "?embed=1&hide_header=1" and "?hide_header=1&embed=1"
  // are the same page, and `layoutKey` already treats them as one. The reported
  // URL still loads the page it names — these parameters are read by name.
  kept.sort();
  return kept.toString();
}

/**
 * Where the SPA is mounted — `App.tsx` hands `PUBLIC_URL` to the router as its
 * `basename`, and production share links land on `www.har.com/aimode/share/…`.
 *
 * Every route rule below is written against the path the router sees, so the
 * prefix is taken off before they run and put back on afterwards: the reported
 * URL has to stay a real one, since Matomo renders a heatmap by loading it.
 */
const BASENAME = (process.env.PUBLIC_URL ?? "").replace(/\/+$/, "");

/**
 * The path as the router sees it, or `null` when it is not under the mount.
 *
 * Read exactly the way the router reads it, because a path this declines to
 * recognise is reported with no rule applied to it at all — token and all.
 * React Router's `stripBasename` compares
 * `pathname.toLowerCase().startsWith(basename.toLowerCase())` and then slices
 * the original at the basename's length, so two things follow:
 *
 * - The comparison ignores case. With PUBLIC_URL=/aimode, "/AIMODE/share/<token>"
 *   is a routed share page, and reading it case-sensitively left it outside the
 *   app — the one spelling for which the token reached Matomo unscrubbed.
 * - The boundary is a segment boundary. A bare `startsWith` swallowed the
 *   prefix of a path that merely began with the same letters: "/aimodel/x"
 *   became "/l/x", and the basename went back on to make "/aimode/l/x" — an
 *   address that exists nowhere, in a report whose URLs are supposed to be
 *   loadable.
 *
 * `null` rather than the path unchanged, so the caller that puts the basename
 * back can tell that it was never taken off. The two have to agree: stripping
 * nothing and re-adding anyway is how "/aimodel/x" became "/aimode/aimodel/x".
 */
function routePathname(pathname: string): string | null {
  if (!BASENAME) return pathname;
  if (!pathname.toLowerCase().startsWith(BASENAME.toLowerCase())) return null;
  const boundary = pathname.charAt(BASENAME.length);
  if (boundary && boundary !== "/") return null;
  return pathname.slice(BASENAME.length) || "/";
}

/** The router's view of a path, falling back to the path for one outside it. */
function stripBasename(pathname: string): string {
  return routePathname(pathname) ?? pathname;
}

function canonicalPathname(pathname: string): string {
  const route = routePathname(pathname);
  // Not under the mount, so the router never routed it: no rule here applies,
  // and the address is reported exactly as it arrived.
  if (route === null) return pathname;
  const canonical = canonicalRoutePath(route);
  // Put back in its configured spelling, for the same reason a route name is:
  // "/AIMODE/listing/1" and "/aimode/listing/1" are one page to the router, and
  // the spelling the mount is configured with is the one that is certainly
  // servable — Matomo renders a heatmap by loading the URL it was given.
  return canonical === "/" ? `${BASENAME}/` : `${BASENAME}${canonical}`;
}

/**
 * Every static route name `App.tsx` declares, in the spelling it declares it
 * in, keyed by a spelling it can arrive in.
 *
 * Reported canonically, so "/Share/<token>" and "/share/<token>" — the same
 * rendered page, since route matching ignores case — are one page in the Pages
 * report and one heatmap rather than two. The declared spelling rather than
 * the folded one because `pageTitleFor` puts these paths in the page title:
 * "AI Mode - /html/richcontent/SingleCard" is the name of a thing, and
 * "/html/richcontent/singlecard" is a typo of it.
 *
 * Everything else keeps the spelling it arrived with: a harid, a session id
 * and an unrecognised path are data, not route names.
 */
const ROUTE_NAME = new Map(
  [
    "listing",
    "search",
    "share",
    "html",
    "richcontent",
    "SearchedProperties",
    "Elements",
    "FiltersOverlay",
    "PropertyOverlay",
    "SingleCard",
    "MultipleProperties",
    "AllCards",
    "PropertyChat",
  ].map((name) => [name.toLowerCase(), name] as const),
);

/** The names a route can start with; everything static hangs off one of them. */
const TOP_LEVEL_SEGMENT = new Set(["listing", "share", "html"]);

/** `html/<page>` — the fragment routes the overlays render. */
const HTML_PAGE = new Set([
  "searchedproperties",
  "elements",
  "filtersoverlay",
  "propertyoverlay",
]);

/** `html/richcontent/<card>` — the fragments a chat message embeds. */
const RICH_CONTENT_PAGE = new Set([
  "singlecard",
  "multipleproperties",
  "allcards",
  "propertychat",
]);

/**
 * Whether `App.tsx` declares a route name at this position, given the whole
 * path — a question about the position, not just about the word.
 *
 * Folding on the word alone folded dynamic ids that happen to spell one.
 * "/listing/SEARCH/<session>" matches `listing/:harid/:session?` with the
 * harid "SEARCH", and case-folding it merged that listing's heatmap with the
 * one for a harid of "search": two different listings, one report. The routes
 * these positions come from:
 *
 *   share/:token · listing/search · listing/:harid/share/:token ·
 *   listing/:harid/:session? · html/<page> · html/richcontent/<card>
 *
 * so a top-level name names a route only with something after it (alone it is
 * a `:session` value), "search" only as the whole of "/listing/search", the
 * second "share" only in the four-segment listing share route, and an `html`
 * page only at the depth its own route has. Mirrors the table in `App.tsx`; a
 * route added there and not here is reported as it arrives, which is the safe
 * direction — a spelling too many, never a page renamed.
 */
function isRouteName(lower: string[], index: number): boolean {
  if (index === 0) return lower.length > 1 && TOP_LEVEL_SEGMENT.has(lower[0]);
  if (lower[0] === "listing") {
    if (index === 1) return lower.length === 2 && lower[1] === "search";
    return index === 2 && lower.length === 4 && lower[2] === "share";
  }
  if (lower[0] === "html") {
    if (index === 1) {
      return (
        (lower.length === 2 && HTML_PAGE.has(lower[1])) ||
        (lower.length === 3 && lower[1] === "richcontent")
      );
    }
    return (
      index === 2 &&
      lower.length === 3 &&
      lower[1] === "richcontent" &&
      RICH_CONTENT_PAGE.has(lower[2])
    );
  }
  return false;
}

/**
 * A path segment as a route rule should read it: case-folded and nothing else.
 *
 * Case-folded because React Router really does match a static segment either
 * way. Not decoded, because it does *not* decode — `matchPathImpl` runs its
 * regex against the raw pathname — so `/listing/%73earch` is a listing page
 * with the harid `%73earch`, not the `/listing/search` entry point, and a
 * rule that read it decoded would report a listing page as the home screen.
 */
function routeSegment(segment: string): string {
  return segment.toLowerCase();
}

/**
 * The same segment read as loosely as possible, for the one rule that is only
 * ever allowed to *drop* something.
 *
 * Deciding what a page **is** has to follow the router exactly, per above. But
 * the segment after `share` is a bearer credential, and truncating a path that
 * merely looks like a share route costs a heatmap nobody asked for, while
 * failing to truncate one puts a replayable key to a private conversation in
 * an analytics report. Where the two errors are that lopsided, over-matching
 * is the cheap side. Malformed escapes fall back to the raw text.
 */
function credentialSegment(segment: string): string {
  try {
    return decodeURIComponent(segment).toLowerCase();
  } catch {
    return segment.toLowerCase();
  }
}

/**
 * A path's segments, with the ones that name a route in their canonical
 * spelling and everything else left exactly as it arrived.
 *
 * Shared by the reported URL and the de-duplication key, which have to agree
 * on this the way they already agree on the layout flags. `/Listing/123` and
 * `/listing/123` are one page to the router and one URL to Matomo, so a key
 * that told them apart would cut a recording over a change of capitalisation.
 *
 * Pooling them is only honest while the app renders them alike, which is what
 * `isListingPath` in `utils/routes.ts` is for: the flags around the listing
 * assistant used to read the path case-sensitively, so "/Listing/123" rendered
 * the listing page with general-assistant behaviour — two different DOMs that
 * this would have reported as one heatmap.
 */
function routeSegments(pathname: string): string[] {
  // Only the leading empty segment goes, and trailing slashes. An empty one in
  // the middle is a real difference: "/listing//123" matches no route —
  // `:harid` cannot be empty — so it renders the 404 page, and collapsing it
  // to "/listing/123" reported that 404 as the listing assistant and pointed
  // the heatmap's iframe at a page the visitor never saw.
  const raw = pathname.replace(/\/+$/, "").split("/");
  if (raw[0] === "") raw.shift();
  const lower = raw.map(routeSegment);
  return raw.map((segment, index) =>
    isRouteName(lower, index)
      ? ROUTE_NAME.get(lower[index]) ?? lower[index]
      : segment,
  );
}

/** The canonical form of a path as the router sees it, without the basename. */
function canonicalRoutePath(pathname: string): string {
  const segments = routeSegments(pathname);
  // No route in `App.tsx` sets `caseSensitive`, and React Router's default is
  // to match a static segment either way — so every rule below has to. A
  // "/Share/<token>" renders the shared conversation exactly as "/share/…"
  // does, and a rule that only knew the lower-case spelling would let the
  // token through to Matomo.
  const lower = segments.map(routeSegment);

  if (isBackendEntryPoint(`/${lower.join("/")}`)) return "/";

  // "/<session>" -> "/"
  if (segments.length === 1 && SESSION_SEGMENT.test(lower[0])) return "/";

  // "/listing/<harid>/<session>" -> "/listing/<harid>"
  if (
    lower[0] === "listing" &&
    segments.length === 3 &&
    SESSION_SEGMENT.test(lower[2])
  ) {
    // A harid of literally "search" has no honest canonical URL: this page is
    // a listing assistant, and the only shorter address for it, the
    // "/listing/search" reported here, is the one `App.tsx` reserves for
    // `<Navigate to="/" />`. Reported anyway, rather than folded onto "/".
    // Folding would pour listing-assistant clicks into the *home* heatmap,
    // corrupting the busiest page in the report to tidy up a page that cannot
    // be built for a harid that does not occur; keeping it separate confines
    // the problem to a heatmap nobody will ask for.
    return `/${segments[0]}/${segments[1]}`;
  }

  // A share token is a bearer credential, not an identifier:
  // GET /messages/shared/<token> decrypts it and returns the conversation with
  // no further authorisation (shared_messages_route.py). Reporting it would
  // leave a replayable key to a private conversation sitting in the Pages
  // report, in event URLs and in recordings, readable by anyone with access to
  // the analytics. Dropped at the cost of heatmaps on share pages, which
  // Matomo renders by loading the URL it was given.
  // Read decoded, unlike every rule above it: this one only ever drops.
  const share = segments.map(credentialSegment).indexOf("share");
  if (share !== -1) {
    return `/${segments.slice(0, share + 1).join("/")}`;
  }

  return `/${segments.join("/")}`;
}

/**
 * A stable title per page type. Taken from the route rather than
 * `document.title` because react-helmet writes the title in its own effect,
 * which may not have run yet when a route change fires a page view.
 */
export function pageTitleFor(pathname: string = window.location.pathname): string {
  const route = stripBasename(pathname);
  const canonical = canonicalRoutePath(route);
  // Checked before the listing case: /listing/<harid>/share/<token> is the
  // same read-only shared view as /share/<token>, not the listing assistant.
  //
  // Judged on the route rather than the canonical path, and against the two
  // real routes rather than the presence of a "share" segment. This is the
  // opposite of how the truncation above matches, deliberately: dropping
  // everything after any "share" can only ever lose data, so breadth there is
  // free insurance against a share route this file has not been told about,
  // while claiming a page *is* a shared conversation on the same evidence
  // mislabels "/listing/<harid>/share", which the generic listing route
  // renders as an ordinary assistant.
  const lower = route.split("/").filter(Boolean).map(routeSegment);
  const isShareRoute =
    (lower.length === 2 && lower[0] === "share") ||
    (lower.length === 4 && lower[0] === "listing" && lower[2] === "share");
  if (isShareRoute) return "AI Mode - Shared Conversation";
  const lowerCanonical = canonical.toLowerCase();
  if (lowerCanonical.startsWith("/listing/")) return "AI Mode - Listing Assistant";
  if (lowerCanonical.startsWith("/html/")) return `AI Mode - ${canonical}`;
  return "AI Mode - Home Search";
}

/* -------------------------------------------------------------------------- */
/* Tracking                                                                    */
/* -------------------------------------------------------------------------- */

let lastPageViewUrl: string | null = null;
let lastPageViewPath: string | null = null;
let lastPageViewLayout: string | null = null;

/**
 * The referrer to report for the first page view of a visit.
 *
 * One of our own URLs gets the same treatment as a page URL: the visitor can
 * arrive at `/?question=…` from another AI Mode page, and `urlref` would carry
 * the prompt that `canonicalPageUrl` keeps out of `url`. An external referrer
 * is left exactly as the browser reported it — it is someone else's page, and
 * it is the attribution data Matomo's Referrers report is built on.
 *
 * Except for a URL of ours on another HAR host, which is not someone else's
 * page at all: production share links land on `www.har.com/aimode/share/<token>`,
 * a different origin from the app's own `aimode.har.com`, so our own URLs
 * arrive here as external referrers. Following one into the app would have
 * filed a replayable key to a private conversation in the Referrers report —
 * the leak the page URL rules close, arriving through the one door held open
 * for attribution. Any HAR host is therefore treated as possibly carrying one:
 * the token after `share` goes, and the query is reduced to what a page URL
 * may report. Which site and which page referred the visit survives.
 */
function reportableReferrer(referrer: string): string {
  if (!referrer) return "";
  try {
    const url = new URL(referrer);
    if (url.origin === window.location.origin) return canonicalPageUrl(referrer);
    if (!isHarHost(url.hostname)) return referrer;

    const segments = url.pathname.split("/").filter(Boolean);
    // Decoded, like the truncation in `canonicalRoutePath` and for the same
    // reason: this drops a token, it never decides what a page is.
    const share = segments.map(credentialSegment).indexOf("share");
    if (share !== -1) {
      url.pathname = `/${segments.slice(0, share + 1).join("/")}`;
      // The token is in the path, so the query gets the same allowlist as any
      // other URL of ours rather than being thrown away with it: a share link
      // posted in a campaign carries `utm_*`, and that is attribution, which
      // is the whole reason a referrer is reported at all.
      url.search = reportableQuery(url.searchParams);
    } else {
      // The same allowlist the address bar gets, because this may be one of
      // our own pages on a sibling host — `www.har.com/aimode/?question=…` is
      // cross-origin to `aimode.har.com` and would otherwise arrive whole.
      // It also drops the filters off the `www.har.com/search/…` links
      // `MlsSearchCard` builds: which page referred the visit is attribution,
      // the criteria behind it are the visitor's search.
      //
      // The path is left as it came. Dropping a parameter can only lose data,
      // so breadth is cheap; rewriting another site's path with this app's
      // route rules would rename pages that were never ours.
      url.search = reportableQuery(url.searchParams);
    }
    url.hash = "";
    return url.toString();
  } catch {
    return "";
  }
}

/** Whether a referrer's host is one of ours, and so may carry a share token. */
function isHarHost(hostname: string): boolean {
  const host = hostname.toLowerCase();
  return host === "har.com" || host.endsWith(".har.com");
}

/** A pathname with any trailing slashes removed; "/" stays "/". */
function normalisePath(pathname: string): string {
  return `/${pathname.split("/").filter(Boolean).join("/")}`;
}

/**
 * What counts as "the same page" for de-duplication: the real path, except
 * that the backend entry point counts as the "/" it redirects to.
 *
 * Not the canonical path, which also erases the session id — that difference
 * is the only thing telling a new chat apart from the conversation it
 * replaced (see `isSessionIdAppearing`).
 *
 * Route names are canonicalised the same way the reported URL canonicalises
 * them, and for the same reason: `/Listing/123` and `/listing/123` are one
 * page to the router and one URL to Matomo, so a key that told them apart
 * would report the move between them as a navigation and cut the recording.
 */
function pageKey(pathname: string): string {
  const path = `/${routeSegments(stripBasename(pathname)).join("/")}`;
  return isBackendEntryPoint(path) ? "/" : path;
}

/**
 * The layout half of "the same page", stable under parameter order.
 *
 * `canonicalPageUrl` keeps these, so they are part of which page this is: a
 * move from `/?embed=1` to `/` is a different layout at the same path, and
 * de-duplicating on the path alone would drop it — leaving the recording
 * running under a layout the visitor has left.
 *
 * Built from the same parameters `canonicalPageUrl` reports, by the same
 * function. The two have to agree: a parameter this dropped but that kept
 * would report the same URL twice, and one this kept but that dropped would
 * cut a recording for a move between two URLs reported identically.
 */
function layoutKey(params: URLSearchParams): string {
  return keptLayoutParams(params).toString();
}

/**
 * Whether this navigation is the chat picking up its session id — `/` ->
 * `/<session>`, or `/listing/<harid>` -> `/listing/<harid>/<session>`.
 *
 * That happens the instant the first message is sent, and it is not a new
 * page: reporting it would restart the session recording in the middle of the
 * conversation it is recording.
 *
 * Only *that* direction. Every other move between paths that share a canonical
 * URL is a real navigation: starting a new chat goes the other way,
 * `/<session>` -> `/` (`handleNewChatSession` in DefaultLayout), and picking a
 * conversation from the sidebar goes sideways, `/<a>` -> `/<b>`. Both deserve
 * their own page view and their own recording.
 */
function isSessionIdAppearing(from: string, to: string): boolean {
  const segments = to.split("/").filter(Boolean);
  const session = segments[segments.length - 1];
  if (!session || !SESSION_SEGMENT.test(session)) return false;
  return normalisePath(`/${segments.slice(0, -1).join("/")}`) === normalisePath(from);
}

/**
 * Report a page view, following Matomo's SPA guide: set the referrer to the
 * page we came from, override the URL and title, track, then re-arm the parts
 * of the tracker that bind to the DOM of the page that just rendered.
 *
 * Two navigations are deliberately not reported: one that changes the query
 * string without changing a layout flag (not a navigation at all — the first
 * message strips the prompt parameters as it goes), and the chat picking up
 * its session id, which would cut a recording in half.
 *
 * A query-only change that *does* flip a layout flag — `/?embed=1` -> `/` —
 * is a real navigation and must stay reported: the URL it reports differs, so
 * suppressing it would leave the recording running under a layout the visitor
 * has left. Both judgements are made on the real path rather than the
 * canonical one, because the canonical URL deliberately erases exactly the
 * difference that tells a new chat apart from the conversation it replaced.
 */
export function trackPageView(options?: {
  url?: string;
  title?: string;
  /**
   * True only for a real route change. It is what tells Heatmap & Session
   * Recording to end the current page view and start another, so a virtual
   * page view — an error, a download — leaves the recording running. That is
   * the whole point of turning the plugin's auto-detection off: with it on,
   * every `trackPageView` cut a recording, whatever it meant.
   *
   * Off by default, so a page view added later cannot split recordings by
   * forgetting a flag. `useMatomoPageView`, the one caller that is a
   * navigation, passes it.
   */
  isNavigation?: boolean;
  /**
   * True when this navigation is the chat picking up its session id rather
   * than the visitor going anywhere — React Router's `noRerender` state, which
   * `ChatWindow` sets on exactly that `replace`.
   *
   * The path shape alone cannot tell it apart from opening an existing
   * conversation from the sidebar while sitting on "/": both read `/` ->
   * `/<uuid>`. One must not cut the recording; the other is a real navigation
   * that deserves its own.
   */
  sameConversation?: boolean;
}): void {
  if (!trackerUsable()) return;

  try {
    // Canonicalised even when the caller supplies it, so a virtual page view
    // cannot route around the campaign-parameter allowlist.
    const href = options?.url ?? window.location.href;
    const url = canonicalPageUrl(href);
    const location = new URL(href, window.location.origin);
    const path = pageKey(location.pathname);
    const layout = layoutKey(location.searchParams);
    const isNavigation = options?.isNavigation ?? false;

    if (isNavigation) {
      if (path === lastPageViewPath && layout === lastPageViewLayout) return;
      if (
        options?.sameConversation &&
        lastPageViewPath !== null &&
        layout === lastPageViewLayout &&
        isSessionIdAppearing(lastPageViewPath, path)
      ) {
        // Not reported, but remember where we are: the navigation back out of
        // this session is a new chat, and it has to be told apart from a repeat.
        lastPageViewPath = path;
        return;
      }
    }

    const referrer = lastPageViewUrl ?? reportableReferrer(document.referrer);
    if (isNavigation) {
      // Only a navigation moves the "where we are" the next one is judged
      // against; a virtual page view is not somewhere the visitor went.
      lastPageViewUrl = url;
      lastPageViewPath = path;
      lastPageViewLayout = layout;
    }

    const _paq = paq();
    if (referrer) _paq.push(["setReferrerUrl", referrer]);
    _paq.push(["setCustomUrl", url]);
    // From the URL being reported, not the address bar: a virtual page view
    // for another route would otherwise carry this page's title.
    _paq.push([
      "setDocumentTitle",
      options?.title ?? pageTitleFor(new URL(href, window.location.origin).pathname),
    ]);
    _paq.push(["trackPageView"]);
    // No `enableLinkTracking`, which the SPA guide would normally put here.
    // It reports the clicked href verbatim, which is a way out of every URL
    // rule above: the app links to HAR.com search results built from the
    // visitor's own filters, and those URLs would reach Matomo unscrubbed.
    // What the links mean is already tracked as events at each call site.
    if (isNavigation) {
      // Auto-detection is off (see initMatomo), so this is what starts a new
      // heatmap page view and re-evaluates whether to record this visitor —
      // and what a virtual page view must not do.
      _paq.push(["HeatmapSessionRecording::setNewPageView"]);
    }
  } catch {
    // Tracking must never break the app
  }
}

let currentUserId: string | null = null;

/**
 * The id an event may be attributed to, given the user context as it stands.
 *
 * Nothing while the context is reloading. `useUserManager` keeps the previous
 * user's fields and only raises `loading`, so a `cid` change — which can
 * resolve to somebody else, or to a guest — leaves the outgoing account's
 * username readable by every call site in the meantime. An unattributed event
 * is a gap in a report; one attributed to the wrong person is worse than a gap.
 *
 * Call sites pass this rather than reading `username` themselves, and rather
 * than leaving it to the id `DefaultLayout` sets: React runs child effects
 * before parent ones, so a page's visit event fires before the layout has
 * handed Matomo the user it just resolved.
 */
export function matomoUserId(
  user: { username?: string; loading?: boolean } | undefined,
): string | undefined {
  return user?.loading ? undefined : user?.username;
}

/**
 * Attach the signed-in username to the visit. Matomo keeps it for the rest of
 * the visit, so this is worth calling as soon as the user context resolves —
 * the first page view usually fires before we know who the visitor is.
 */
export function setMatomoUserId(uid: string | undefined): void {
  // Recorded even when the tracker is unusable, because the proxy fallback
  // reads it: `_paq` is not the only place this has to be known, and a
  // sign-out still has to clear it for a visitor whose matomo.js was blocked.
  if (uid === currentUserId) return;
  if (!trackerUsable()) {
    currentUserId = uid || null;
    return;
  }

  if (!uid) {
    // Signing out has to clear it. Matomo keeps a User ID for the rest of the
    // visit, so the next person at a shared computer would otherwise have
    // their page views, events and recording filed under the account that
    // just left.
    if (currentUserId !== null) {
      currentUserId = null;
      paq().push(["resetUserId"]);
    }
    return;
  }

  currentUserId = uid;
  paq().push(["setUserId", uid]);
}

/**
 * Send a tracking event to Matomo (fire-and-forget).
 *
 * The event is reported against the canonical page URL, never the raw address
 * bar, which would carry the prompt parameters (`question`, `q`,
 * `listing_filter`) that `canonicalPageUrl` exists to keep out of Matomo. Each
 * transport carries that URL itself — the proxy puts it on the request, and
 * the tracker pins it with `setCustomUrl` immediately before the event rather
 * than inheriting whatever the last page view left behind.
 *
 * @param actionName - Category tree name, e.g. "AI Mode / Home Visit"
 * @param options.uid - Username for logged-in users; omit for guests.
 * @param options.url - Override the page URL. Canonicalised like any other, so
 *   it cannot carry what the visitor typed.
 */
export function trackEvent(
  actionName: string,
  options?: {
    uid?: string;
    url?: string;
  },
): void {
  try {
    const segments = actionName.split("/").map((segment) => segment.trim());
    const category = segments[0];
    const action = segments[1] ? `${category} - ${segments[1]}` : "";
    const name = segments[2] ? `${action} - ${segments[2]}` : "";

    const event: IMatomoEvent = {
      category,
      action,
      name,
      url: canonicalPageUrl(options?.url ?? window.location.href),
      // Resolved here, at the moment the event happened, rather than by the
      // transport that ends up sending it. An event buffered while matomo.js
      // is still in flight is replayed through the proxy once the script
      // fails, which can be seconds later and on the other side of a sign-out
      // or a different sign-in; reading the live user id at that point would
      // file it under whoever is signed in *then*.
      uid: options?.uid || currentUserId || undefined,
    };

    if (matomoTrackerEnabled && trackerState !== "failed") {
      sendEventViaTracker(event);
      return;
    }

    if (!matomoTrackerEnabled && !isProduction) {
      // The proxy refuses to forward outside production anyway.
      return;
    }

    sendEventViaProxy(event);
  } catch {
    // Tracking must never break the app
  }
}

function sendEventViaTracker(event: IMatomoEvent): void {
  // Only when the event carries one. Most call sites use the guest-style
  // `trackEvent(name)`, and handing that `undefined` to a function that now
  // treats it as a sign-out would clear the visit's user id on the first such
  // event after signing in. Sign-in and sign-out are the layout's effect.
  // Past the buffer cap the fallback copy would be dropped, so an event that
  // cannot be remembered goes straight down the proxy instead: pushing it to
  // `_paq` as well would either be lost (script errors) or sent twice (script
  // loads). Only reachable if 50 events are tracked before matomo.js lands.
  if (trackerState === "pending" && bufferedEvents.length >= MAX_BUFFERED_EVENTS) {
    sendEventViaProxy(event);
    return;
  }

  if (event.uid) setMatomoUserId(event.uid);

  const _paq = paq();
  // Matomo fills `urlref` from `document.referrer` until `setReferrerUrl`
  // overrides it, so an event that beats the first page view would carry an
  // unscrubbed same-origin referrer — `/?question=…` — even though its own URL
  // is clean. Set it here in that case; the page view sets the same value.
  if (lastPageViewUrl === null) {
    _paq.push(["setReferrerUrl", reportableReferrer(document.referrer)]);
  }
  // Pin the URL to this event rather than inheriting whatever the last page
  // view left behind. React runs child effects before parent ones, so a page
  // component's visit event can be queued before the layout's page view for
  // the same route — and before any page view at all on the first load, where
  // the tracker would otherwise fall back to the raw address bar.
  _paq.push(["setCustomUrl", event.url]);
  // Matomo drops an event without an action; only a single-segment action name
  // could produce one, and then the category is the most faithful stand-in.
  _paq.push(["trackEvent", event.category, event.action || event.category, event.name]);

  if (trackerState === "pending") {
    bufferedEvents.push(event);
  }
}

/** Generate a 16-char hex visitor ID (persisted in sessionStorage) */
function getVisitorId(): string {
  const key = "matomo_visitor_id";
  let id = sessionStorage.getItem(key);
  if (!id || id.length !== 16) {
    id = Array.from(crypto.getRandomValues(new Uint8Array(8)))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
    sessionStorage.setItem(key, id);
  }
  return id;
}

/**
 * Where a fallback event goes.
 *
 * The fallback runs because a blocker stopped `matomo.js`, so it has to be
 * first-party — the same blocker would stop `matomo.har.com/matomo.php`. Both
 * endpoints below are. The mirror is preferred once the tracker is configured
 * because the legacy endpoint refuses to forward outside production, which
 * would silently drop every fallback event in the environment a rollout is
 * being rehearsed in.
 */
function proxyEventUrl(): string {
  return matomoTrackerEnabled
    ? `${MATOMO_PROXY_URL}/matomo.php`
    : MATOMO_PROXY_URL;
}

/**
 * Sends one event down the first-party proxy. Fire-and-forget: tracking must
 * never break the app, so a refusal is swallowed.
 */
function sendEventViaProxy(event: IMatomoEvent): void {
  try {
    const params = new URLSearchParams({
      // The same site the tracker reports to. Only reachable in production
      // while the tracker is unconfigured — `trackEvent` returns early
      // elsewhere — so this is site 8 there, whatever the transport.
      idsite: siteId,
      rec: "1",
      apiv: "1",
      e_c: event.category,
      e_a: event.action,
      e_n: event.name,
      url: event.url,
      _id: getVisitorId(),
      rand: Math.random().toString(36).substring(2),
    });
    // On every request, the way Matomo's own tracker sends it: `matomo.js`
    // puts `urlref` on each one and lets the server decide what to do with it.
    // Matomo reads it when a request creates a visit and ignores it otherwise,
    // so the only thing this transport has to get right is that the request
    // which happens to create the visit is carrying it — and the honest way to
    // guarantee that is for all of them to carry it.
    //
    // Counting requests to hand it to exactly one was the alternative, and it
    // was wrong in a new way every time it was fixed: two events queued in the
    // same tick, a refusal while a sibling was still on the wire, and finally a
    // visit timing out under a tab left open for half an hour, after which the
    // next visit could no longer be told where it came from.
    const referrer = reportableReferrer(document.referrer);
    if (referrer) params.set("urlref", referrer);
    // Whoever was signed in when the event happened — `trackEvent` resolves it
    // there, including for the guest-style `trackEvent(name)` call sites that
    // rely on the id the layout set. Not read from `currentUserId` here: this
    // runs at flush time, which for a buffered event is not event time.
    if (event.uid) {
      params.set("uid", event.uid);
    }
    fetch(`${proxyEventUrl()}?${params.toString()}`, {
      method: "GET",
      credentials: "include",
    }).catch(() => {
      // Nothing to retry: the event is gone and the next one carries the
      // referrer anyway.
    });
  } catch {
    // Tracking must never break the app
  }
}
